import { useEffect, useState, forwardRef } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../app/hooks';
import { useTranslation } from 'react-i18next';
import { RouteConstants } from '../../constants/RouteConstants';
import {
  NewOrganizationFormLayout,
  authSettingOTPSchema,
  authSettingPersistentCookieSchema,
  authSettingOTPLockoutSchema
} from '../../util/constants';

import LoaderContainer from '../shared/loaderContainer/LoaderContainer';

import Card from '../../components/shared/cards/Card';
import { HTTP_STATUS } from '../../http/constants/http.status';

import { SettingIcon } from '../../assets/components/svgs';
import { init } from 'i18next';
import { getAuthenticationSettings } from '../../http/user-management-services';
import DynamicFormFieldRenderer from '../Reusable/DynamicFormFieldRenderer';
import ActionButtons from './ActionButtons';
import { handleSettingsValidation, validateSettingsSave } from './Utils';
import { isEmpty } from 'lodash';
import { setBreadcrump } from '../../app/organizationReducer';

const AuthenticationSettings = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [formData, setFormData] = useState<any>({});
  const [initialData, setInitialData] = useState({});
  const [formError, setFormError] = useState<any>({});
  const [inputSchema, setInpuSchema] = useState(authSettingOTPLockoutSchema);

  useEffect(() => {
    const init = async () => {
      const authSettingResp = await getAuthenticationSettings();
      if (authSettingResp.status === HTTP_STATUS.HTTP_OK) {
        setFormData(authSettingResp.data);
        setInitialData(authSettingResp.data);
        setIsLoading(false);
      }
    };
    dispatch(
      setBreadcrump([
        {
          key: RouteConstants.ROUTE_SECURITY_SETTINGS,
          label: t('T_ACCESS_MANAGEMENT_SETTINGS')
        }
      ])
    );

    init();
  }, []);

  useEffect(() => {
    if (!isEmpty(formData)) {
      if (formData['otpRequestLockoutMode'] === 'true') {
        setInpuSchema(
          inputSchema.map((schema: any) => {
            //return { ...schema, style: { display: '' }, disableLabel: false };
            return { ...schema, disabled: false };
          })
        );
      } else {
        setInpuSchema(
          inputSchema.map((schema: any) => {
            //return { ...schema, style: { display: 'none' }, disableLabel: true };
            return { ...schema, disabled: true };
          })
        );
      }
    }
  }, [formData]);

  const postUpdateSettings = () => {
    setInitialData(formData);
    setIsReadOnly(true);
    setIsLoading(false);
  };

  const resetSettings = () => {
    setFormData(initialData);
    setFormError({});
  };

  const handleValidation = () => {
    let otpFormError = handleSettingsValidation(formData, authSettingOTPSchema, t);
    let otpLockoutError = handleSettingsValidation(formData, authSettingOTPLockoutSchema, t);
    let persistentCookieError = handleSettingsValidation(
      formData,
      authSettingPersistentCookieSchema,
      t
    );
    let formError = { ...otpFormError, ...persistentCookieError, ...otpLockoutError };
    setFormError(formError);
    return formError;
  };

  const validateSave = () => {
    return (
      validateSettingsSave(formData, authSettingOTPSchema) ||
      validateSettingsSave(formData, authSettingPersistentCookieSchema)
    );
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <div className="flex w-3/5 flex-col overflow-visible px-2 py-3">
        <Card
          title={t('T_AUTHENTICATION_OTP_SETTINGS')}
          icon={SettingIcon}
          alt={'T_AUTHENTICATION_OTP_SETTINGS'}
          height={''}
          width={''}
          className=""
          actions={
            <ActionButtons
              settingKey={'authSetting'}
              formData={formData}
              callback={postUpdateSettings}
              setIsReadOnly={setIsReadOnly}
              setIsLoading={setIsLoading}
              isReadOnly={isReadOnly}
              initialData={initialData}
              resetSettings={resetSettings}
              handleValidation={handleValidation}
              validateSave={validateSave}
            />
          }
        >
          <div className="mb-5 flex w-full flex-col flex-wrap">
            <div className="flex w-full flex-wrap">
              <DynamicFormFieldRenderer
                dynamicFormData={authSettingOTPSchema}
                formData={formData}
                setFormData={setFormData}
                formError={formError}
                setFormError={setFormError}
                layout={NewOrganizationFormLayout.twoColumn}
                editable={!isReadOnly}
                disabled={isReadOnly}
              />
            </div>
          </div>
        </Card>
        <Card
          title={t('T_AUTHENTICATION_OTP_LOCKOUT_SETTINGS')}
          icon={SettingIcon}
          alt={'T_AUTHENTICATION_OTP_LOCKOUT_SETTINGS'}
          height={''}
          width={''}
          className=""
        >
          <div className="mb-5 flex w-full flex-col flex-wrap">
            <div className="flex w-full flex-wrap">
              <DynamicFormFieldRenderer
                dynamicFormData={inputSchema}
                formData={formData}
                setFormData={setFormData}
                formError={formError}
                setFormError={setFormError}
                layout={NewOrganizationFormLayout.twoColumn}
                editable={!isReadOnly}
                disabled={isReadOnly}
              />
            </div>
          </div>
        </Card>
        <Card
          title={t('T_AUTHENTICATION_PERSISTENT_COOKIE_SETTINGS')}
          icon={SettingIcon}
          alt={'T_AUTHENTICATION_PERSISTENT_COOKIE_SETTINGS'}
          height={''}
          width={''}
          className=""
        >
          <div className="mb-5 flex w-full flex-col flex-wrap">
            <div className="flex w-full flex-wrap">
              <DynamicFormFieldRenderer
                dynamicFormData={authSettingPersistentCookieSchema}
                formData={formData}
                setFormData={setFormData}
                formError={formError}
                setFormError={setFormError}
                layout={NewOrganizationFormLayout.twoColumn}
                editable={!isReadOnly}
                disabled={isReadOnly}
              />
            </div>
          </div>
        </Card>
      </div>
    </LoaderContainer>
  );
};

export default AuthenticationSettings;
